import PageHeader from '../../../components/ui/PageHeader';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// Contexts and Hooks
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';

// API Service (re-cableado a lealify-base — antes ElasticsearchService2/esapi)
import adminUsersApi, { AdminUserFilters, UserData, impersonateUser, impersonationCode } from '../../../api/adminUsersApi';
import { UsoCelda, useUsoNegocios } from '../components/UsoNegocio';

// Types & Helpers
import { getPlanLabel, getPlanBadgeClasses } from '../../../utils/planPermissions';
import { VERTICAL_META, VerticalBadge } from '../../../components/admin/VerticalBadge';

// UI Components & Icons
import Layout from '../../../components/layout/Layout';
import PaginationControls from '../../../components/common/PaginationControls';
import { useNavigate } from 'react-router-dom';
import {
  Users, Search, Building, User, Database, Clock, Calendar, Loader2,
  Filter, X, ChevronDown, ChevronUp, FileDown, Crown, Activity, SlidersHorizontal, Eye, Contact
} from 'lucide-react';

import ClientContactInfo from '../../../components/common/ClientContactInfo';
import BrandLoader from '../../../components/common/BrandLoader';
import ConfirmDialog from '../../../components/common/ConfirmDialog';

// Librería Excel
import * as XLSX from 'xlsx';

// --- Interfaces ---
// UserData / AdminUserFilters vienen del adaptador adminUsersApi (lealify-base).
interface MonthlyUsers {
  monthKey: string;
  displayMonth: string;
  users: UserData[];
  count: number;
}

// --- Constants & Helpers ---
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;
const MAX_BUSINESS_NAME_LENGTH = 25;

const truncateName = (name: string | null, maxLength: number = MAX_BUSINESS_NAME_LENGTH): string => {
  if (!name) return 'N/A';
  return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
};

const formatDisplayMonth = (date: Date): string => 
  new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: 'long' })
    .format(date)
    .replace(/^\w/, c => c.toUpperCase());

const formatDate = (date: Date | null): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

// --- Chip de actividad: recencia de la última actividad del negocio ---
// (lo más reciente entre visita en kiosko, alta de cliente o canje). Antes solo
// miraba la visita en kiosko, lo que marcaba inactivos a negocios que operan 100%
// desde el administrador (cargan puntos / registran clientes a mano).
const ActivityChip: React.FC<{ lastActivityAt?: string | number | Date | null }> = ({ lastActivityAt }) => {
  if (!lastActivityAt) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-canvas text-content-3" title="Sin visitas, altas de cliente ni canjes registrados">Sin actividad</span>;
  }
  const ts = new Date(lastActivityAt as any).getTime();
  if (isNaN(ts)) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-canvas text-content-3">—</span>;
  }
  const days = Math.floor((Date.now() - ts) / 86400000);
  const label = days <= 0 ? 'Hoy' : days === 1 ? 'Ayer' : `Hace ${days}d`;
  const cls = days <= 7
    ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300'
    : days <= 30
      ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300'
      : 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`} title="Última actividad: visita en kiosko, alta de cliente o canje (lo más reciente)">
      <Activity className="h-3 w-3 mr-1" />{label}
    </span>
  );
};

// --- Badge de Suscripción (usa helpers centralizados) ---
const getSubscriptionBadge = (user: UserData) => {
  const plan = user.subscription?.plan || 'free';
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPlanBadgeClasses(plan)}`}>
      {getPlanLabel(plan)}
    </span>
  );
};


// ========================================================================
// Component Definition
// ========================================================================
const AdminUsersPage: React.FC = () => {
  // --- Hooks ---
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { addError, clearErrors } = useError();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const contentTopRef = useRef<HTMLDivElement>(null);
  const searchTriggedRef = useRef(false);

  // --- State ---
  const [users, setUsers] = useState<UserData[]>([]);
  // «Cómo lo usan» (admin F3): una llamada por página con los ids de los negocios visibles.
  const idsUso = useMemo(() => users.map((u) => u.businessId || u.uid).filter(Boolean) as string[], [users]);
  const { uso, cargando: usoCargando } = useUsoNegocios(idsUso);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [pageChanging, setPageChanging] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMorePages, setHasMorePages] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
    
  const [totalUsers, setTotalUsers] = useState<number>(0);

  // --- ESTADO DE FILTROS ---
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
    
  // "Ver como": usuario objetivo pendiente de confirmar (diálogo themed, no window.confirm)
  const [verComo, setVerComo] = useState<UserData | null>(null);
  const [verComoBusy, setVerComoBusy] = useState(false);

  const [filters, setFilters] = useState<AdminUserFilters>({
    plan: 'all',
    vertical: 'all',
    dateFrom: null,
    dateTo: null
  });

  const [activeFilters, setActiveFilters] = useState<AdminUserFilters>({
    plan: 'all', vertical: 'all', dateFrom: null, dateTo: null
  });

  // --- Data Fetching ---
  const fetchUsers = useCallback(async (pageToLoad: number, termToSearch: string, currentFilters: AdminUserFilters) => {
    if (!currentUser || currentUser.role !== 'admin') {
      setUsers([]); setLoading(false); setLoadingPage(false); setTotalUsers(0); setHasMorePages(false);
      return;
    }

    if (clearErrors) clearErrors();

    try {
      const queryParams: AdminUserFilters = {
        term: termToSearch,
        ...currentFilters
      };

      const response = await adminUsersApi.fetchUsersPage(queryParams, pageToLoad, PAGE_SIZE);

      setUsers(response.items);
      setTotalUsers(response.total);
      setHasMorePages(response.hasMore);

    } catch (error: any) {
      if (addError) addError(`Error al cargar usuarios: ${error.message}`, undefined, `AdminUsers-${pageToLoad}`);
      setUsers([]);
      setTotalUsers(0);
      setHasMorePages(false);
    } finally {
      setLoading(false);
      setLoadingPage(false);
      setPageChanging(false);
    }
  }, [currentUser, addError, clearErrors]);


  // --- Effects ---
  useEffect(() => {
      setActiveFilters(filters);
      if (currentPage !== 1) setCurrentPage(1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.plan, filters.vertical, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      if (searchTerm !== activeSearchTerm) {
        setActiveSearchTerm(searchTerm);
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
        searchTriggedRef.current = true;
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm, activeSearchTerm, currentPage]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      if (currentPage === 1 && !activeSearchTerm && !searchTriggedRef.current && !loadingPage) {
         setLoadingPage(true); 
      } else {
         setLoadingPage(true);
      }
      
      fetchUsers(currentPage, activeSearchTerm, activeFilters)
        .finally(() => {
           setLoading(false); 
        });

      searchTriggedRef.current = false;
    } else {
      setUsers([]); setTotalUsers(0); setHasMorePages(false);
      setLoading(false); setLoadingPage(false); setPageChanging(false);
    }
  }, [currentUser?.role, currentPage, activeSearchTerm, activeFilters, fetchUsers]);


  useEffect(() => {
    if (contentTopRef.current && !loading && !loadingPage) {
      contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage, loading, loadingPage]);

  // --- Grouping Logic ---
  const groupedUsers = useMemo<MonthlyUsers[]>(() => {
    if (!users || users.length === 0) return [];
    
    const groups: Record<string, { users: UserData[], firstDateInGroup: Date }> = {};
    
    users.forEach(user => {
        if (user.createdAt && user.createdAt instanceof Date && !isNaN(user.createdAt.getTime())) {
            const date = user.createdAt;
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const monthKey = `${year}-${month}`;
            if (!groups[monthKey]) {
                groups[monthKey] = { users: [], firstDateInGroup: date };
            }
            groups[monthKey].users.push(user);
        }
    });
    
    return Object.entries(groups).map(([monthKey, groupData]) => ({
      monthKey,
      displayMonth: formatDisplayMonth(groupData.firstDateInGroup),
      users: groupData.users.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.getTime() - a.createdAt.getTime();
      }),
      count: groupData.users.length
    })).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [users]);

  // --- EXPORT TO EXCEL FUNCTION ---
  const handleExportExcel = async () => {
    if (!currentUser || currentUser.role !== 'admin' || isExporting) return;
    
    if (clearErrors) clearErrors();
    setIsExporting(true);

    try {
        const limit = totalUsers > 0 ? totalUsers : 2000;
        
        const queryParams: AdminUserFilters = {
            term: activeSearchTerm,
            ...activeFilters
        };

        const response = await adminUsersApi.fetchUsersPage(queryParams, 1, limit);
        const usersToExport = response.items;

        if (usersToExport.length === 0) {
            if (addError) addError("No hay datos para exportar", "warning");
            setIsExporting(false);
            return;
        }

        const headers = ['Nombre', 'Email', 'Teléfono', 'Negocio', 'Vertical', 'Plan', 'Total Clientes', 'Última Actividad', 'Fecha Registro'];

        const data = usersToExport.map(u => [
            u.displayName || 'N/A',
            u.email || 'N/A',
            u.phoneNumber ? `+${u.countryCode || ''} ${u.phoneNumber}` : 'N/A',
            u.businessName || 'N/A',
            VERTICAL_META[u.vertical || 'lealtad']?.label || u.vertical || 'Lealtad',
            u.subscription?.plan?.toUpperCase() || 'FREE',
            u.clientCount || 0,
            u.lastActivityAt ? formatDate(new Date(u.lastActivityAt as any)) : 'Sin actividad',
            formatDate(u.createdAt)
        ]);

        const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

        const colWidths = [{ wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Usuarios Admin');

        const dateStr = new Date().toLocaleDateString('es-MX').replace(/\//g, '-');
        XLSX.writeFile(wb, `usuarios_admin_${dateStr}.xlsx`);

    } catch (error: any) {
        console.error("Export error:", error);
        if (addError) addError("Error al generar el Excel", undefined, "export_error");
    } finally {
        setIsExporting(false);
    }
  };

  // --- Handlers ---
  const handleFilterChange = (key: keyof AdminUserFilters, value: any) => {
      setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearAllFilters = () => {
      setSearchTerm('');
      setFilters({
          plan: 'all',
          dateFrom: null,
          dateTo: null
      });
      setCurrentPage(1);
  };

  const countActiveFilters = () => {
      let count = 0;
      if (filters.plan !== 'all') count++;
      if (filters.vertical && filters.vertical !== 'all') count++;
      if (filters.dateFrom) count++;
      if (filters.dateTo) count++;
      return count;
  };

  // --- Pagination handlers ---
  const handleNextPage = useCallback(() => {
    if (loading || loadingPage || pageChanging) return;
    if (hasMorePages) {
      setPageChanging(true);
      setCurrentPage(prev => prev + 1);
    }
  }, [loading, loadingPage, pageChanging, hasMorePages]);

  const handlePrevPage = useCallback(() => {
    if (loading || loadingPage || pageChanging) return;
    if (currentPage > 1) {
      setPageChanging(true);
      setCurrentPage(prev => prev - 1);
    }
  }, [loading, loadingPage, pageChanging, currentPage]);


  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // --- Render Logic ---
  const canGoPrev = currentPage > 1;
  const canGoNext = hasMorePages;
  const activeFilterCount = countActiveFilters();

  return (
    <Layout>
      <div className="w-full space-y-6">
        <div ref={contentTopRef} id="content-top"></div>
        
        {/* Header */}
        <PageHeader
          icon={Users}
          title="Negocios"
          subtitle="Busca un negocio, abre su ficha o entra como su dueño"
          className="border-b border-line pb-4 animate-fade-in-up"
          style={{animationDelay: '0.1s'}}
          actions={
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={loading || loadingPage || isExporting || users.length === 0}
              className="inline-flex items-center px-4 py-2 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50 w-full sm:w-auto justify-center"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
              )}
              {isExporting ? 'Exportando...' : 'Exportar Excel'}
            </button>
          }
        />

        {/* Buscador y Toggle Filtros */}
        <div className="bg-surface shadow sm:rounded-lg p-4 animate-fade-in-up space-y-4" style={{animationDelay: '0.15s'}}>
          <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className={`h-5 w-5 ${(loadingPage || pageChanging || loading) ? "text-blue-500 animate-pulse" : "text-content-3"}`} />
                </div>
                <input
                  type="search"
                  className="block w-full pl-10 pr-3 py-2 sm:text-sm border-line rounded-md leading-5 bg-surface placeholder-content-3 focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                  placeholder="Buscar por Email, Nombre o Negocio..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  disabled={loading || loadingPage || pageChanging}
                />
              </div>
              
              <button 
                  type="button"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md w-full sm:w-auto justify-center transition-colors ${isFilterOpen || activeFilterCount > 0 ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 text-blue-700 dark:text-blue-300' : 'border-line text-content-2 bg-surface hover:bg-surface-2'}`}
              >
                  <Filter className="h-4 w-4 mr-2" />
                  Filtros
                  {activeFilterCount > 0 && <span className="ml-2 bg-accent-solid text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{activeFilterCount}</span>}
                  {isFilterOpen ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
              </button>
          </div>

          {/* Panel de Filtros Avanzados */}
          {isFilterOpen && (
              <div className="pt-4 border-t border-line grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
                  <div>
                      <label className="block text-xs font-medium text-content-2 mb-1">Plan de Suscripción</label>
                      <select 
                          value={filters.plan}
                          onChange={(e) => handleFilterChange('plan', e.target.value)}
                          className="block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-accent focus:border-accent sm:text-sm rounded-md"
                      >
                          <option value="all">Todos los planes</option>
                          <option value="free">Gratuito (Free)</option>
                          <option value="pro">Profesional (Pro)</option>
                          <option value="premium">Premium (legacy)</option>
                          <option value="onboarding">Onboarding</option>
                      </select>
                  </div>

                  <div>
                      <label className="block text-xs font-medium text-content-2 mb-1">Vertical</label>
                      <select
                          value={filters.vertical || 'all'}
                          onChange={(e) => handleFilterChange('vertical', e.target.value)}
                          className="block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-accent focus:border-accent sm:text-sm rounded-md"
                      >
                          <option value="all">Todas las verticales</option>
                          <option value="lealtad">Lealtad</option>
                          <option value="dental">Dental</option>
                          <option value="letsmove">Baile (Let's Move)</option>
                          <option value="clublectura">Club de lectura</option>
                          <option value="pos">POS</option>
                      </select>
                  </div>

                  {/* Filtros de Fecha */}
                  <div className="flex gap-2 lg:col-span-2">
                      <div className="flex-1">
                          <label className="block text-xs font-medium text-content-2 mb-1">Registrado Desde</label>
                          <input 
                              type="date" 
                              className="block w-full py-2 px-3 border border-line bg-surface rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent sm:text-sm"
                              value={filters.dateFrom ? (filters.dateFrom instanceof Date ? filters.dateFrom.toISOString().split('T')[0] : filters.dateFrom) as string : ''}
                              onChange={(e) => handleFilterChange('dateFrom', e.target.value ? new Date(e.target.value) : null)}
                          />
                      </div>
                      <div className="flex-1">
                          <label className="block text-xs font-medium text-content-2 mb-1">Hasta</label>
                          <input 
                              type="date" 
                              className="block w-full py-2 px-3 border border-line bg-surface rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent sm:text-sm"
                              value={filters.dateTo ? (filters.dateTo instanceof Date ? filters.dateTo.toISOString().split('T')[0] : filters.dateTo) as string : ''}
                              onChange={(e) => handleFilterChange('dateTo', e.target.value ? new Date(e.target.value) : null)}
                          />
                      </div>
                  </div>

                  <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
                      <button type="button" onClick={clearAllFilters} className="text-sm text-content-3 hover:text-content-2 flex items-center">
                          <X className="h-4 w-4 mr-1" />
                          Limpiar filtros
                      </button>
                  </div>
              </div>
          )}
          
          {/* Badges de Filtros Activos */}
          {activeFilterCount > 0 && !isFilterOpen && (
              <div className="flex flex-wrap gap-2 pt-2">
                  {filters.plan !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300">Plan: {filters.plan}</span>}
                  {filters.vertical && filters.vertical !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300">Vertical: {VERTICAL_META[filters.vertical]?.label || filters.vertical}</span>}
                  {(filters.dateFrom || filters.dateTo) && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-canvas text-content">Fechas activas</span>}
                  <button type="button" onClick={clearAllFilters} className="text-xs text-content-3 underline ml-2">Limpiar</button>
              </div>
          )}
        </div>
        
        {/* Desktop View */}
        <div className="hidden md:block">
          <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <div className="px-4 py-4 sm:px-6 bg-surface-2 border-b border-line">
              <div className="flex justify-between items-center">
                <h3 className="text-lg leading-6 font-medium text-content flex items-center"><User className="h-5 w-5 mr-2 text-content-3" /> Usuarios</h3>
                {(!loading || users.length > 0) && (
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-1.5 text-blue-500" />
                    <span className="text-sm text-content-3">
                      Total: <span className="font-medium text-blue-700 dark:text-blue-300">{totalUsers}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {loading && users.length === 0 && (
              <div className="py-12 text-center">
                <BrandLoader size={36} />
                <p className="mt-3 text-sm font-medium text-content-3">Cargando usuarios...</p>
              </div>
            )}

            {!loadingPage && !pageChanging && users.length === 0 && !loading && (
              <div className="text-center py-16">
                <Search className="h-12 w-12 text-content-3 mx-auto mb-3" />
                <p className="text-lg font-medium text-content-2">No se encontraron usuarios</p>
                <p className="text-sm text-content-3 mt-1">
                  Prueba ajustando los filtros.
                </p>
              </div>
            )}

            {(!loading || users.length > 0) && groupedUsers.length > 0 && (
              <div className="space-y-8 p-6">
                {groupedUsers.map((group, groupIndex) => (
                  <div key={group.monthKey} className="animate-fade-in-up" style={{ animationDelay: `${0.3 + groupIndex * 0.1}s` }}>
                    <div className="flex items-center mb-3">
                      <h3 className="text-lg font-medium text-content flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-content-3" />
                        {group.displayMonth}
                      </h3>
                      <div className="ml-3 px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full flex items-center">
                        <Users className="h-3 w-3 mr-1" />
                        {group.count}
                      </div>
                    </div>

                    <div className="overflow-x-auto border rounded-lg">
                      <table className="min-w-full divide-y divide-line">
                        <thead className="bg-surface-2">
                          <tr>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Usuario</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Negocio</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Plan</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Clientes</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Actividad</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider" title="Último rastro, actividad del giro y riesgo de renovación; pasa el ratón o toca para el detalle">Uso</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Registro</th>
                            <th scope="col" className="px-2 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider"><span className="sr-only">Acciones</span><Crown className="h-3.5 w-3.5 inline text-content-3" /></th>
                          </tr>
                        </thead>
                        <tbody className="bg-surface divide-y divide-line">
                          {group.users.map((user) => (
                            <tr key={user.uid} className="hover:bg-surface-2 transition-colors duration-150 align-middle">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {user.phoneNumber ? (
                                    <ClientContactInfo
                                      clientName={user.displayName}
                                      phoneNumber={user.phoneNumber}
                                      countryCode={user.countryCode || null}
                                      email={user.email}
                                      showName={true}
                                      nameAsLink={false}
                                      avatarUrl={user.avatarUrl || null}
                                    />
                                  ) : (
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0 h-10 w-10 bg-canvas rounded-full flex items-center justify-center overflow-hidden">
                                        {user.avatarUrl ? <img src={user.avatarUrl} alt="" className="h-10 w-10 object-cover" loading="lazy" data-testid="contacto-avatar" /> : <User className="h-6 w-6 text-content-3" />}
                                      </div>
                                      <div className="ml-4">
                                        <div className="text-sm font-medium text-content">{user.displayName}</div>
                                        <p className="text-sm text-content-3 mt-1 truncate">{user.email}</p>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex items-center text-sm text-content">
                                    {user.businessId && <Building className="h-4 w-4 text-content-3 mr-1.5 flex-shrink-0" />}
                                    <span className="truncate max-w-[150px]" title={user.businessName || ''}>
                                      {truncateName(user.businessName)}
                                    </span>
                                  </div>
                                  {/* El POS se marca APARTE del giro: un negocio de lealtad o
                                      dental puede tener punto de venta sin dejar de ser lo que es. */}
                                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                    <VerticalBadge vertical={user.vertical} />
                                    {user.hasPos && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-500/20 dark:text-slate-300"
                                        title="Tiene punto de venta enlazado">
                                        POS
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                   {getSubscriptionBadge(user)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <Database className="h-4 w-4 text-blue-500 mr-2" />
                                        <span className="text-sm font-medium text-content">{user.clientCount}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <ActivityChip lastActivityAt={user.lastActivityAt} />
                                </td>
                                <td className="px-4 py-3">
                                  <UsoCelda tenantId={user.businessId || user.uid} uso={uso[String(user.businessId || user.uid)]} cargando={usoCargando} nombre={user.businessName} />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-content-3">
                                  <div className="flex items-center">
                                    <Clock className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" />
                                    {formatDate(user.createdAt)}
                                  </div>
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-center">
                                  <div className="inline-flex items-center gap-1">
                                  {user.businessId && (
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/dashboard/admin/negocios/${user.businessId}`)}
                                      title="Ficha del negocio: plan, pagos, módulos, actividad"
                                      data-testid="negocio-ficha"
                                      className="inline-flex items-center justify-center p-2 border border-accent/40 rounded-md text-accent bg-accent/10 hover:bg-accent/20 transition-colors"
                                    >
                                      <Contact className="h-4 w-4" />
                                    </button>
                                  )}
                                  {user.businessId && (
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/dashboard/admin/tenants/${user.businessId}/modules`)}
                                      title="Módulos y configuración de este negocio"
                                      className="inline-flex items-center justify-center p-2 border border-line rounded-md text-content-2 bg-surface hover:bg-surface-2 transition-colors"
                                    >
                                      <SlidersHorizontal className="h-4 w-4 text-content-3" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/dashboard/admin/subscriptions?term=${encodeURIComponent(user.email)}`)}
                                    title="Gestionar suscripción de este usuario"
                                    className="inline-flex items-center justify-center p-2 border border-line rounded-md text-content-2 bg-surface hover:bg-surface-2 transition-colors"
                                  >
                                    <Crown className="h-4 w-4 text-yellow-500" />
                                  </button>
                                  {/* Ver como el dueño. Lealtad = el manager se recarga como él (Fase A);
                                      otras verticales = pestaña nueva a su fachada vía código de canje (Fase B). */}
                                  {user.ownerUserId != null && (
                                    <button
                                      type="button"
                                      onClick={() => setVerComo(user)}
                                      title={`Ver como ${user.displayName || 'el dueño'}`}
                                      className="inline-flex items-center justify-center p-2 border border-line rounded-md text-content-2 bg-surface hover:bg-surface-2 transition-colors"
                                    >
                                      <Eye className="h-4 w-4 text-content-3" />
                                    </button>
                                  )}
                                  </div>
                                </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{animationDelay: '0.15s'}}>
           <div className="px-4 py-4 bg-surface-2 border-b border-line">
             <div className="flex justify-between items-center">
               <h3 className="text-base font-medium text-content flex items-center">
                 <User className="h-5 w-5 mr-2 text-content-3" /> Usuarios
               </h3>
               {(!loading || users.length > 0) && (
                 <div className="flex items-center">
                   <Users className="h-4 w-4 mr-1 text-blue-500" />
                   <span className="text-sm text-content-3">{totalUsers}</span>
                 </div>
               )}
             </div>
           </div>
           
           {loading && users.length === 0 && (
             <div className="py-8 text-center">
               <BrandLoader size={36} />
               <p className="mt-2 text-sm font-medium text-content-3">Cargando usuarios...</p>
             </div>
           )}
           
           {!loadingPage && !pageChanging && users.length === 0 && !loading && (
            <div className="p-6 text-center">
              <Search className="h-12 w-12 text-content-3 mx-auto mb-3" />
              <p className="text-base font-medium text-content-2">No se encontraron usuarios</p>
            </div>
           )}
           
           {users.length > 0 && (
             <div className="space-y-4 pt-2 pb-4">
               {groupedUsers.map((group) => (
                 <div key={group.monthKey}>
                   <div className="px-4 py-2 bg-canvas flex items-center border-t border-b border-line">
                     <h3 className="text-base font-medium text-content">{group.displayMonth}</h3>
                     <div className="ml-3 px-2 py-0.5 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full flex items-center">
                       <Users className="h-3 w-3 mr-1" /> 
                       {group.count}
                     </div>
                   </div>
                   <div className="divide-y divide-line">
                     {group.users.map((user) => (
                       <div key={user.uid} className="p-4 hover:bg-surface-2 transition-all duration-150">
                         <div className="flex justify-between items-start gap-4">
                           {/* Columna Izquierda */}
                           <div className="flex-1 min-w-0 overflow-hidden pr-2">
                             {user.phoneNumber ? (
                               <ClientContactInfo
                                 clientName={user.displayName}
                                 phoneNumber={user.phoneNumber}
                                 countryCode={user.countryCode || null}
                                 email={user.email} 
                                 showName={true}
                                 nameAsLink={false}
                                 iconSize="sm"
                                 avatarUrl={user.avatarUrl || null}
                               />
                             ) : (
                               <div>
                                 <div className="text-sm font-medium text-content truncate pr-1">{user.displayName}</div>
                                 <div className="text-xs text-content-3 mt-0.5 truncate" title={user.email}>{user.email}</div>
                               </div>
                             )}
                             <div className="mt-2 flex flex-col gap-1">
                                {user.businessName && (
                                    <div className="flex items-center text-xs text-content-3">
                                        <Building className="h-3.5 w-3.5 mr-1 text-content-3 flex-shrink-0" />
                                        <span className="truncate">{truncateName(user.businessName, 20)}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-1.5">{getSubscriptionBadge(user)}<VerticalBadge vertical={user.vertical} /></div>
                             </div>
                           </div>
                           
                           {/* Columna Derecha */}
                           <div className="flex flex-col items-end flex-shrink-0 gap-1.5">
                             <div className="flex items-center bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded-full text-xs font-medium">
                               <Database className="h-3 w-3 mr-1" />
                               {user.clientCount}
                             </div>
                             <ActivityChip lastActivityAt={user.lastActivityAt} />
                           </div>
                         </div>
                         {/* Cómo lo usan (toca para el detalle) */}
                         <div className="mt-2">
                           <UsoCelda tenantId={user.businessId || user.uid} uso={uso[String(user.businessId || user.uid)]} cargando={usoCargando} nombre={user.businessName} />
                         </div>
                         {/* Fila inferior: registro + acciones. flex-wrap: con 3 botones
                             (Módulos/Suscripción/Ver como) no caben en una línea de 390px. */}
                         <div className="mt-3 pt-2 border-t border-line/60 flex flex-wrap items-center justify-between gap-y-2">
                           <span className="text-xs text-content-3 flex items-center">
                             <Clock className="h-3 w-3 mr-1 text-content-3" />
                             Registro: {formatDate(user.createdAt)}
                           </span>
                           <div className="flex flex-wrap items-center justify-end gap-1.5">
                             {user.businessId && (
                               <button
                                 type="button"
                                 onClick={() => navigate(`/dashboard/admin/negocios/${user.businessId}`)}
                                 data-testid="negocio-ficha-movil"
                                 className="inline-flex items-center px-2.5 py-1 border border-accent/40 rounded-md text-xs font-medium text-accent bg-accent/10 hover:bg-accent/20 transition-colors"
                               >
                                 <Contact className="h-3 w-3 mr-1" /> Ficha
                               </button>
                             )}
                             {user.businessId && (
                               <button
                                 type="button"
                                 onClick={() => navigate(`/dashboard/admin/tenants/${user.businessId}/modules`)}
                                 className="inline-flex items-center px-2.5 py-1 border border-line rounded-md text-xs font-medium text-content-2 bg-surface hover:bg-surface-2 transition-colors"
                               >
                                 <SlidersHorizontal className="h-3 w-3 mr-1 text-content-3" /> Módulos
                               </button>
                             )}
                             <button
                               type="button"
                               onClick={() => navigate(`/dashboard/admin/subscriptions?term=${encodeURIComponent(user.email)}`)}
                               className="inline-flex items-center px-2.5 py-1 border border-line rounded-md text-xs font-medium text-content-2 bg-surface hover:bg-surface-2 transition-colors"
                             >
                               <Crown className="h-3 w-3 mr-1 text-yellow-500" /> Suscripción
                             </button>
                             {/* Ver como — igual que la tabla desktop (lealtad=recarga; resto=pestaña nueva) */}
                             {user.ownerUserId != null && (
                               <button
                                 type="button"
                                 onClick={() => setVerComo(user)}
                                 className="inline-flex items-center px-2.5 py-1 border border-line rounded-md text-xs font-medium text-content-2 bg-surface hover:bg-surface-2 transition-colors"
                               >
                                 <Eye className="h-3 w-3 mr-1 text-content-3" /> Ver como
                               </button>
                             )}
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               ))}
             </div>
           )}
        </div>

        {/* Pagination Controls Desktop */}
        {(!loading || users.length > 0) && totalUsers > 0 && (
            <div className="hidden md:block mt-6"> 
              <PaginationControls 
                currentPage={currentPage}
                isLoading={loadingPage || pageChanging}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                canGoPrev={canGoPrev && !pageChanging}
                canGoNext={canGoNext && !pageChanging}
                itemsPerPage={PAGE_SIZE}
                countOnPage={users.length}
                totalItems={totalUsers}
              />
            </div>
        )}
        
        {/* Pagination Controls Mobile */}
        {(!loading || users.length > 0) && totalUsers > 0 && (
          <div className="md:hidden mt-6">
            <PaginationControls
              currentPage={currentPage}
              isLoading={loadingPage || pageChanging}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              canGoPrev={canGoPrev && !pageChanging}
              canGoNext={canGoNext && !pageChanging}
              itemsPerPage={PAGE_SIZE}
              countOnPage={users.length}
              totalItems={totalUsers}
              isMobile={true}
            />
          </div>
        )}

        {/* Page changing indicator */}
        {pageChanging && (
          <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50">
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
            <span className="text-sm font-medium text-content-2">Cambiando página...</span>
          </div>
        )}

        {/* Confirmación "Ver como" (reemplaza window.confirm nativo) */}
        <ConfirmDialog
          open={verComo != null}
          busy={verComoBusy}
          title="Ver como"
          icon={<Eye className="h-5 w-5 text-green-600 dark:text-green-400 mr-2.5 flex-shrink-0" />}
          message={
            <>
              Vas a ver{' '}
              {(verComo?.vertical ?? 'lealtad') === 'lealtad' ? 'el manager' : 'su app'} como{' '}
              <strong className="text-content">{verComo?.displayName || verComo?.email}</strong>
              {verComo?.businessName ? <> ({verComo.businessName})</> : null}.
              {(verComo?.vertical ?? 'lealtad') !== 'lealtad' && (
                <> Se abrirá en una pestaña nueva.</>
              )}
              <br />
              Las acciones que hagas quedarán a su nombre.
            </>
          }
          confirmLabel="Ver como"
          onCancel={() => setVerComo(null)}
          onConfirm={async () => {
            if (!verComo?.ownerUserId) return;
            setVerComoBusy(true);
            const esLealtad = (verComo.vertical ?? 'lealtad') === 'lealtad';
            try {
              if (esLealtad) {
                // Fase A: mismo dominio — el manager se recarga como el dueño.
                await impersonateUser(verComo.ownerUserId as number);
                window.location.href = '/';
              } else {
                // Fase B: fachada en otro dominio. Abrir la pestaña SÍNCRONO (antes del
                // await) para no perder el gesto del usuario ante el popup-blocker.
                const win = window.open('', '_blank');
                try {
                  const { url } = await impersonationCode(verComo.ownerUserId as number);
                  if (win) win.location.href = url; else window.open(url, '_blank');
                } catch (e) {
                  win?.close();
                  throw e;
                }
                setVerComoBusy(false);
                setVerComo(null);
              }
            } catch (e) {
              addError((e as Error).message || 'No se pudo iniciar Ver como');
              setVerComoBusy(false);
              setVerComo(null);
            }
          }}
        />
      </div>
    </Layout>
  );
};

export default AdminUsersPage;